var React = require('react');
var { useRef, useState, useEffect } = React;

function useCanvasPanZoom(canvasRef) {
  var panPosRef = useRef({ x: 0, y: 0 });
  var zoomRef = useRef(1);
  var [zoomLevel, setZoomLevel] = useState(1);
  var panRef = useRef(null);
  var worldRef = useRef(null);
  var zoomLayerRef = useRef(null);
  var autoPanAnimRef = useRef(null);
  var pinchRef = useRef(null);
  var spaceHeldRef = useRef(false);
  var panSubscribersRef = useRef([]);

  var MIN_ZOOM = 0.25;
  var MAX_ZOOM = 1;

  function buildWorldTransform(x, y) {
    return 'translate(' + Math.round(x) + 'px, ' + Math.round(y) + 'px)';
  }

  // Panning never re-renders React (pan lives in a ref), so overlays that must
  // track a node on screen — the AI Flow Builder balloon — subscribe here and
  // reposition themselves imperatively alongside the world transform.
  function subscribeToPan(fn) {
    panSubscribersRef.current.push(fn);
    fn(panPosRef.current.x, panPosRef.current.y, zoomRef.current);
    return function() {
      panSubscribersRef.current = panSubscribersRef.current.filter(function(f) { return f !== fn; });
    };
  }

  function applyWorldStyle(x, y, zoom) {
    if (worldRef.current) worldRef.current.style.transform = buildWorldTransform(x, y);
    if (zoomLayerRef.current) zoomLayerRef.current.style.zoom = zoom;
    panSubscribersRef.current.forEach(function(fn) { fn(x, y, zoom); });
  }

  function applyTransform() {
    applyWorldStyle(panPosRef.current.x, panPosRef.current.y, zoomRef.current);
  }

  function changeZoom(newZoom, pivotX, pivotY) {
    var clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));
    var oldZoom = zoomRef.current;
    if (clamped === oldZoom) return;

    if (pivotX !== undefined && pivotY !== undefined) {
      var panX = panPosRef.current.x;
      var panY = panPosRef.current.y;
      panPosRef.current = {
        x: pivotX - (pivotX - panX) * (clamped / oldZoom),
        y: pivotY - (pivotY - panY) * (clamped / oldZoom)
      };
    }

    zoomRef.current = clamped;
    setZoomLevel(clamped);
    applyTransform();
  }

  function autoPanAnimateTo(toX, toY, toZoom, dur) {
    if (autoPanAnimRef.current) cancelAnimationFrame(autoPanAnimRef.current);
    var fromX = panPosRef.current.x, fromY = panPosRef.current.y, fromZ = zoomRef.current;
    var t0 = null;
    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
    function step(now) {
      if (!t0) t0 = now;
      var t = Math.min((now - t0) / dur, 1);
      var e = easeOutCubic(t);
      var x = fromX + (toX - fromX) * e;
      var y = fromY + (toY - fromY) * e;
      var z = fromZ + (toZoom - fromZ) * e;
      panPosRef.current = { x: x, y: y };
      zoomRef.current = z;
      applyWorldStyle(x, y, z);
      if (t < 1) { autoPanAnimRef.current = requestAnimationFrame(step); }
      else        { autoPanAnimRef.current = null; setZoomLevel(toZoom); }
    }
    autoPanAnimRef.current = requestAnimationFrame(step);
  }

  // Pan handlers (mouse)
  function onPanMove(event) {
    var pan = panRef.current;
    if (!pan || !worldRef.current) return;
    var x = pan.originX + (event.clientX - pan.startX);
    var y = pan.originY + (event.clientY - pan.startY);
    pan.lastX = x;
    pan.lastY = y;
    panPosRef.current = { x: x, y: y };
    applyWorldStyle(x, y, zoomRef.current);
  }

  function stopPanning(onStopCallback) {
    if (!panRef.current) return;
    var didPan = Math.abs(panRef.current.lastX - panRef.current.originX) > 3 ||
                 Math.abs(panRef.current.lastY - panRef.current.originY) > 3;
    panPosRef.current = { x: panRef.current.lastX, y: panRef.current.lastY };
    if (canvasRef.current) canvasRef.current.classList.remove('workflow-canvas-panning');
    panRef.current = null;
    window.removeEventListener('mousemove', onPanMove);
    window.removeEventListener('mouseup', _stopPanningMouse);
    if (!didPan && onStopCallback) {
      onStopCallback();
    }
  }

  var _stopPanCallbackRef = useRef(null);
  function _stopPanningMouse() {
    stopPanning(_stopPanCallbackRef.current);
  }

  function startPanning(event, onStopCallback) {
    _stopPanCallbackRef.current = onStopCallback || null;
    panRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: panPosRef.current.x,
      originY: panPosRef.current.y,
      lastX: panPosRef.current.x,
      lastY: panPosRef.current.y
    };
    if (canvasRef.current) canvasRef.current.classList.add('workflow-canvas-panning');
    window.addEventListener('mousemove', onPanMove);
    window.addEventListener('mouseup', _stopPanningMouse);
  }

  // Pan handlers (touch)
  function onPanTouchMove(event) {
    if (pinchRef.current) return;
    if (event.touches.length >= 2) {
      stopPanningTouch();
      return;
    }
    var pan = panRef.current;
    if (!pan || !worldRef.current) return;
    event.preventDefault();
    var touch = event.touches[0];
    var x = pan.originX + (touch.clientX - pan.startX);
    var y = pan.originY + (touch.clientY - pan.startY);
    pan.lastX = x;
    pan.lastY = y;
    panPosRef.current = { x: x, y: y };
    applyWorldStyle(x, y, zoomRef.current);
  }

  var _stopPanTouchCallbackRef = useRef(null);
  function stopPanningTouch() {
    if (!panRef.current) return;
    var didPan = Math.abs(panRef.current.lastX - panRef.current.originX) > 3 ||
                 Math.abs(panRef.current.lastY - panRef.current.originY) > 3;
    panPosRef.current = { x: panRef.current.lastX, y: panRef.current.lastY };
    if (canvasRef.current) canvasRef.current.classList.remove('workflow-canvas-panning');
    panRef.current = null;
    window.removeEventListener('touchmove', onPanTouchMove);
    window.removeEventListener('touchend', stopPanningTouch);
    if (!didPan && _stopPanTouchCallbackRef.current) {
      _stopPanTouchCallbackRef.current();
    }
  }

  // Abort an in-progress touch pan WITHOUT committing it — used when a node's
  // long-press fires and node-dragging should take over the same touch. Snaps
  // the pan back to where it started so the canvas doesn't drift, and skips the
  // tap/stop callback (this wasn't a tap on empty canvas).
  function cancelPanningTouch() {
    if (!panRef.current) return;
    panPosRef.current = { x: panRef.current.originX, y: panRef.current.originY };
    applyWorldStyle(panRef.current.originX, panRef.current.originY, zoomRef.current);
    if (canvasRef.current) canvasRef.current.classList.remove('workflow-canvas-panning');
    panRef.current = null;
    window.removeEventListener('touchmove', onPanTouchMove);
    window.removeEventListener('touchend', stopPanningTouch);
  }

  function startPanningTouch(event, onStopCallback) {
    _stopPanTouchCallbackRef.current = onStopCallback || null;
    var touch = event.touches[0];
    panRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      originX: panPosRef.current.x,
      originY: panPosRef.current.y,
      lastX: panPosRef.current.x,
      lastY: panPosRef.current.y
    };
    if (canvasRef.current) canvasRef.current.classList.add('workflow-canvas-panning');
    window.addEventListener('touchmove', onPanTouchMove, { passive: false });
    window.addEventListener('touchend', stopPanningTouch);
  }

  // Pinch zoom (touch)
  function onPinchTouchMove(event) {
    var pinch = pinchRef.current;
    if (!pinch || event.touches.length < 2) return;
    event.preventDefault();
    var t1 = event.touches[0];
    var t2 = event.touches[1];
    var dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    var ratio = dist / pinch.startDist;
    changeZoom(pinch.startZoom * ratio, pinch.pivotX, pinch.pivotY);
  }

  function stopPinchTouch() {
    pinchRef.current = null;
    window.removeEventListener('touchmove', onPinchTouchMove);
    window.removeEventListener('touchend', stopPinchTouch);
  }

  function startPinch(event) {
    var t1 = event.touches[0];
    var t2 = event.touches[1];
    var dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    var rect = canvasRef.current.getBoundingClientRect();
    pinchRef.current = {
      startDist: dist,
      startZoom: zoomRef.current,
      pivotX: ((t1.clientX + t2.clientX) / 2) - rect.left,
      pivotY: ((t1.clientY + t2.clientY) / 2) - rect.top
    };
    // Cancel any existing pan
    if (panRef.current) {
      panPosRef.current = { x: panRef.current.lastX, y: panRef.current.lastY };
      if (canvasRef.current) canvasRef.current.classList.remove('workflow-canvas-panning');
      panRef.current = null;
      window.removeEventListener('touchmove', onPanTouchMove);
      window.removeEventListener('touchend', stopPanningTouch);
    }
    window.addEventListener('touchmove', onPinchTouchMove, { passive: false });
    window.addEventListener('touchend', stopPinchTouch);
  }

  // Wheel zoom
  useEffect(function() {
    var el = canvasRef.current;
    if (!el) return;
    function onWheel(event) {
      if (event.target && typeof event.target.closest === 'function' && event.target.closest('.planner-overlay')) {
        return;
      }
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      var rect = el.getBoundingClientRect();
      var pivotX = event.clientX - rect.left;
      var pivotY = event.clientY - rect.top;
      var delta = event.deltaY > 0 ? -0.08 : 0.08;
      changeZoom(zoomRef.current + delta, pivotX, pivotY);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return function() { el.removeEventListener('wheel', onWheel); };
  }, []);

  // Center canvas on a node position — accounts for current zoom so the
  // node lands at the canvas midpoint regardless of zoom level.
  function centerOnNode(pos, nodeW, nodeH) {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var z = zoomRef.current;
    var canvasW = canvas.offsetWidth;
    var canvasH = canvas.offsetHeight;
    var x = canvasW / 2 - (pos.left + (nodeW || 220) / 2) * z;
    var y = canvasH / 2 - (pos.top + (nodeH || 48) / 2) * z;
    panPosRef.current = { x: x, y: y };
    applyWorldStyle(x, y, z);
  }

  // Zoom + pan so a world-space bounding box fits inside the canvas with some
  // padding. bbox = { left, top, width, height } in the same coordinate space
  // node positions live in. Used by the Format button to frame the whole
  // workflow after re-arranging. Honors the same MIN/MAX_ZOOM clamp as manual
  // zoom, so very large flows settle at MIN_ZOOM rather than fitting exactly.
  function fitToView(bbox, opts) {
    var canvas = canvasRef.current;
    if (!canvas || !bbox || !(bbox.width > 0) || !(bbox.height > 0)) return;
    var options = opts || {};
    var padding = options.padding != null ? options.padding : 80;
    var canvasW = canvas.offsetWidth;
    var canvasH = canvas.offsetHeight;
    var availW = Math.max(1, canvasW - padding * 2);
    var availH = Math.max(1, canvasH - padding * 2);
    var fitZoom = Math.min(availW / bbox.width, availH / bbox.height);
    fitZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, fitZoom));
    var centerX = bbox.left + bbox.width / 2;
    var centerY = bbox.top + bbox.height / 2;
    var toX = canvasW / 2 - centerX * fitZoom;
    var toY = canvasH / 2 - centerY * fitZoom;
    if (options.animate === false) {
      panPosRef.current = { x: toX, y: toY };
      zoomRef.current = fitZoom;
      setZoomLevel(fitZoom);
      applyWorldStyle(toX, toY, fitZoom);
    } else {
      autoPanAnimateTo(toX, toY, fitZoom, options.duration || 450);
    }
  }

  // Cleanup
  useEffect(function() {
    return function() {
      window.removeEventListener('mousemove', onPanMove);
      window.removeEventListener('mouseup', _stopPanningMouse);
      window.removeEventListener('touchmove', onPanTouchMove);
      window.removeEventListener('touchend', stopPanningTouch);
      window.removeEventListener('touchmove', onPinchTouchMove);
      window.removeEventListener('touchend', stopPinchTouch);
      if (autoPanAnimRef.current) cancelAnimationFrame(autoPanAnimRef.current);
    };
  }, []);

  return {
    panPosRef: panPosRef,
    zoomRef: zoomRef,
    zoomLevel: zoomLevel,
    panRef: panRef,
    worldRef: worldRef,
    zoomLayerRef: zoomLayerRef,
    autoPanAnimRef: autoPanAnimRef,
    pinchRef: pinchRef,
    spaceHeldRef: spaceHeldRef,
    applyWorldStyle: applyWorldStyle,
    subscribeToPan: subscribeToPan,
    changeZoom: changeZoom,
    autoPanAnimateTo: autoPanAnimateTo,
    startPanning: startPanning,
    startPanningTouch: startPanningTouch,
    cancelPanningTouch: cancelPanningTouch,
    startPinch: startPinch,
    centerOnNode: centerOnNode,
    fitToView: fitToView
  };
}

module.exports = useCanvasPanZoom;
